/**
 * 🎬 WALKTHROUGH SYSTEM TYPES
 * Epic 8 Implementation - Progressive Feature Disclosure
 * 
 * Complete TypeScript definitions for walkthrough system
 * Supports Epic 7.5 baseline through Epic 16 evolution
 */

export interface WalkthroughStep {
  id: string;
  target: string; // CSS selector for element to highlight
  content: string;
  title?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: 'click' | 'hover' | 'focus' | 'none';
  wait?: number; // milliseconds to wait before next step
  skipIf?: string; // feature flag condition to skip this step
}

export interface Walkthrough {
  id: string;
  title: string;
  description: string;
  category: 'foundation' | 'workflow' | 'orchestration' | 'advanced';
  epic: number; // Epic number when this walkthrough becomes available
  steps: WalkthroughStep[];
  requiredFlags?: string[]; // Feature flags required to access this walkthrough
  estimatedDuration?: number; // seconds
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
}

export interface WalkthroughState {
  activeWalkthrough: Walkthrough | null;
  currentStepIndex: number;
  isPlaying: boolean;
  isComplete: boolean;
  completedWalkthroughs: string[];
  userPreferences: WalkthroughPreferences;
  overlayVisible: boolean;
}

export interface WalkthroughPreferences {
  autoAdvance: boolean;
  showTooltips: boolean;
  animationSpeed: 'slow' | 'normal' | 'fast';
  skipCompleted: boolean;
  language: string;
}

export interface FeatureFlags {
  // Epic 7.5 Baseline Features
  'dashboard-navigation': boolean;
  'basic-agent-access': boolean;
  'brand-intelligence': boolean;
  
  // Epic 8 Features
  'agent-activation': boolean;
  'workflow-basics': boolean;
  'demo-mode': boolean;
  
  // Epic 9 Features  
  'workflow-canvas': boolean;
  'agent-collaboration': boolean;
  'multi-agent-handoffs': boolean;
  
  // Epic 10 Features
  'ecosystem-navigator': boolean;
  'emma-orchestrator': boolean;
  'command-center': boolean;
  
  // Epic 11 Features
  'triad-orchestration': boolean;
  'customer-vendor-partner': boolean;
  'emma-coordination': boolean;
  
  // Epic 12-16 Features (Progressive)
  'advanced-analytics': boolean;
  'ai-recommendations': boolean;
  'enterprise-integration': boolean;
  'marketplace-access': boolean;
  'global-orchestration': boolean;
}

export interface WalkthroughContextType {
  // State
  state: WalkthroughState;
  
  // Actions
  startWalkthrough: (walkthroughId: string) => void;
  stopWalkthrough: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipToStep: (stepIndex: number) => void;
  completeWalkthrough: () => void;
  
  // Preferences
  updatePreferences: (preferences: Partial<WalkthroughPreferences>) => void;
  
  // Feature Flags
  featureFlags: FeatureFlags;
  isFeatureEnabled: (flag: keyof FeatureFlags) => boolean;
  enableFeature: (flag: keyof FeatureFlags) => void;
  disableFeature: (flag: keyof FeatureFlags) => void;
  
  // Walkthrough Management
  getAvailableWalkthroughs: () => Walkthrough[];
  getWalkthroughById: (id: string) => Walkthrough | null;
  isWalkthroughCompleted: (id: string) => boolean;
  
  // Utility
  showOverlay: () => void;
  hideOverlay: () => void;
}

// Predefined Walkthrough Configurations
export const EPIC_WALKTHROUGHS: Record<string, Walkthrough> = {
  // Epic 7.5 Baseline
  'platform-overview': {
    id: 'platform-overview',
    title: 'Platform Overview',
    description: 'Get familiar with the CrowsNest ecosystem',
    category: 'foundation',
    epic: 7.5,
    estimatedDuration: 180,
    difficulty: 'beginner',
    steps: [
      {
        id: 'welcome',
        target: '.dashboard-header',
        title: 'Welcome to Cosellus',
        content: 'Your intelligent AI marketing orchestration platform with 35+ specialized agents ready to transform your marketing operations.',
        position: 'bottom'
      },
      {
        id: 'navigation',
        target: '.main-navigation',
        title: 'Navigate Your Ecosystem',
        content: 'Access your dashboard, agents, orchestration tools, and project management from this main navigation.',
        position: 'right'
      },
      {
        id: 'brand-intelligence',
        target: '[href="/brand-intelligence"]',
        title: 'Brand Intelligence Hub',
        content: 'Discover how AI agents analyze your brand positioning and competitive landscape.',
        position: 'bottom',
        action: 'hover'
      }
    ]
  },

  // Epic 7.5 Baseline Comprehensive Demo
  'epic-7-5-baseline': {
    id: 'epic-7-5-baseline',
    title: 'Epic 7.5 Platform Foundation Demo',
    description: 'Comprehensive demonstration of current platform capabilities through Epic 7.5',
    category: 'foundation',
    epic: 7.5,
    estimatedDuration: 240,
    difficulty: 'beginner',
    requiredFlags: ['dashboard-navigation', 'basic-agent-access', 'brand-intelligence'],
    steps: [
      {
        id: 'ecosystem-welcome',
        target: '.dashboard-header',
        title: 'Intelligent AI Marketing Orchestration',
        content: 'Welcome to Cosellus - the AI-powered marketing intelligence platform. 35+ specialized agents coordinate to transform your marketing from chaos to orchestrated excellence.',
        position: 'bottom'
      },
      {
        id: 'agent-ecosystem-overview',
        target: '.main-navigation',
        title: '51+ Specialized AI Agents',
        content: 'Navigate through a comprehensive library of 51+ specialized AI agents covering every aspect of your business operations - from brand intelligence to stakeholder coordination to content generation.',
        position: 'right'
      },
      {
        id: 'brand-intelligence-demo',
        target: '[href="/brand-intelligence"]',
        title: 'AI-Powered Brand Intelligence',
        content: 'Explore sophisticated brand analysis capabilities. Our AI agents provide competitive analysis, market positioning insights, and strategic brand intelligence that adapts to your industry and market.',
        position: 'bottom',
        action: 'hover'
      },
      {
        id: 'stakeholder-mapping-preview',
        target: '.ecosystem-preview, .stakeholder-section',
        title: 'Stakeholder Relationship Intelligence',
        content: 'Preview Epic 7\'s stakeholder mapping capabilities. The platform will intelligently map complex B2B buying committees (13+ stakeholders) with relationship visualization and multi-persona content coordination.',
        position: 'top'
      },
      {
        id: 'content-generation-workflow',
        target: '[href="/agents"], .content-generation-section',
        title: 'Orchestrated Content Generation',
        content: 'Experience coordinated content creation workflows. Multiple specialized agents collaborate to generate persona-specific content that aligns with stakeholder preferences and business objectives.',
        position: 'bottom'
      },
      {
        id: 'knowledge-graph-foundation',
        target: '.knowledge-graph-section, [href="/orchestration"]',
        title: 'Knowledge Graph Intelligence',
        content: 'Explore the knowledge graph foundation that powers intelligent entity relationships, context preservation, and sophisticated business intelligence across your entire ecosystem.',
        position: 'top'
      },
      {
        id: 'future-ecosystem-vision',
        target: '.ecosystem-command-center, .future-preview',
        title: 'Complete Ecosystem Management Vision',
        content: 'Vision preview: Full ecosystem coordination including Client Organizations, Target Customers, Vendors, and Partners. Emma Meta-Orchestrator will coordinate across all entities with triad intelligence.',
        position: 'center'
      },
      {
        id: 'progressive-enhancement',
        target: '.demo-controller, .walkthrough-progress',
        title: 'Progressive Platform Evolution',
        content: 'This foundation evolves through Epic 16 with visual workflow orchestration, real-time ecosystem intelligence, and enterprise-grade capabilities. Each epic adds sophisticated coordination layers.',
        position: 'center'
      }
    ]
  },
  
  // Epic 8 Dashboard Navigation
  'dashboard-navigation': {
    id: 'dashboard-navigation',
    title: 'Dashboard Navigation Walkthrough',
    description: 'Get familiar with your CrowsNest dashboard interface',
    category: 'foundation',
    epic: 8,
    estimatedDuration: 180,
    difficulty: 'beginner',
    requiredFlags: ['dashboard-navigation', 'basic-agent-access'],
    steps: [
      {
        id: 'dashboard-header',
        target: '.dashboard-header',
        title: 'Welcome to Your Dashboard',
        content: 'This is your central command center for managing your AI-powered business ecosystem.',
        position: 'bottom'
      },
      {
        id: 'navigation-menu',
        target: '.navigation-menu',
        title: 'Navigation Menu',
        content: 'Access all platform features including agents, orchestration, and analytics from this menu.',
        position: 'right'
      },
      {
        id: 'user-profile',
        target: '.user-profile-widget',
        title: 'Your Profile',
        content: 'Monitor your account status and manage user preferences.',
        position: 'bottom'
      },
      {
        id: 'system-status',
        target: '.system-status-widget',
        title: 'System Health',
        content: 'Real-time monitoring of platform health and performance metrics.',
        position: 'top'
      },
      {
        id: 'agent-activity',
        target: '.agent-activity-widget',
        title: 'Agent Activity',
        content: 'Live view of your active agents and their current tasks.',
        position: 'left'
      }
    ]
  },

  // Epic 8 Account Management
  'account-management': {
    id: 'account-management',
    title: 'Account Management Walkthrough',
    description: 'Learn to manage your account settings and organization',
    category: 'foundation',
    epic: 8,
    estimatedDuration: 210,
    difficulty: 'beginner',
    requiredFlags: ['dashboard-navigation', 'basic-agent-access'],
    steps: [
      {
        id: 'account-navigation',
        target: '[href="/account"]',
        title: 'Account Settings',
        content: 'Access your account settings to manage profile, organization, and preferences.',
        position: 'bottom',
        action: 'hover'
      },
      {
        id: 'organization-setup',
        target: '.organization-setup',
        title: 'Organization Management',
        content: 'Configure your organization settings for multi-tenant collaboration.',
        position: 'right'
      },
      {
        id: 'user-preferences',
        target: '.user-preferences-panel',
        title: 'Personal Preferences',
        content: 'Customize your platform experience with personal settings.',
        position: 'top'
      },
      {
        id: 'tenant-demo',
        target: '.tenant-demo-controls',
        title: 'Multi-Tenant Features',
        content: 'Explore multi-tenant capabilities for enterprise collaboration.',
        position: 'left'
      }
    ]
  },

  // Epic 7.5 Stakeholder Mapping Demonstration
  'stakeholder-mapping-demo': {
    id: 'stakeholder-mapping-demo',
    title: 'Stakeholder Mapping Intelligence',
    description: 'Experience intelligent stakeholder relationship mapping and analysis',
    category: 'workflow',
    epic: 7.5,
    estimatedDuration: 300,
    difficulty: 'intermediate',
    requiredFlags: ['dashboard-navigation', 'basic-agent-access'],
    steps: [
      {
        id: 'stakeholder-discovery',
        target: '.stakeholder-analysis-section, .council-agent-panel',
        title: 'Intelligent Stakeholder Discovery',
        content: 'Watch as Council Agent analyzes your organization documents to automatically identify all key stakeholders in complex B2B buying committees (up to 13+ decision makers).',
        position: 'top'
      },
      {
        id: 'relationship-visualization',
        target: '.stakeholder-network-visualization, .stakeholder-map',
        title: 'Relationship Network Mapping',
        content: 'Explore sophisticated relationship visualization showing influence patterns, consensus builders, potential blockers, and stakeholder interconnections across the buying committee.',
        position: 'bottom'
      },
      {
        id: 'multi-persona-coordination',
        target: '.multi-persona-content-section, .content-coordination-panel',
        title: 'Multi-Persona Content Strategy',
        content: 'See how the platform coordinates different content approaches for each stakeholder persona, ensuring messaging resonates with individual concerns and priorities.',
        position: 'right'
      },
      {
        id: 'consensus-building',
        target: '.consensus-tracking, .stakeholder-engagement-panel',
        title: 'Consensus Building Intelligence',
        content: 'Monitor consensus building progress across stakeholders, identify alignment opportunities, and track engagement patterns for strategic decision coordination.',
        position: 'left'
      },
      {
        id: 'stakeholder-handoff',
        target: '.content-generation-handoff, [href="/agents"]',
        title: 'Stakeholder-to-Content Integration',
        content: 'Experience seamless handoff from stakeholder analysis to persona-specific content generation, maintaining context and ensuring message alignment.',
        position: 'bottom',
        action: 'hover'
      }
    ]
  },

  // Epic 7.5 Knowledge Graph Exploration
  'knowledge-graph-exploration': {
    id: 'knowledge-graph-exploration',
    title: 'Knowledge Graph Intelligence',
    description: 'Explore sophisticated knowledge graph capabilities and entity relationships',
    category: 'advanced',
    epic: 7.5,
    estimatedDuration: 270,
    difficulty: 'intermediate',
    requiredFlags: ['dashboard-navigation', 'basic-agent-access'],
    steps: [
      {
        id: 'entity-extraction',
        target: '.knowledge-graph-section, .entity-extraction-panel',
        title: 'Intelligent Entity Extraction',
        content: 'Experience how the platform automatically extracts and categorizes business entities - companies, people, technologies, markets - from your documents and conversations.',
        position: 'top'
      },
      {
        id: 'relationship-intelligence',
        target: '.entity-relationships-view, .relationship-graph',
        title: 'Entity Relationship Intelligence',
        content: 'Explore rich entity relationships showing how companies connect to people, technologies link to use cases, and markets relate to opportunities.',
        position: 'bottom'
      },
      {
        id: 'context-preservation',
        target: '.context-manager, .cross-epic-navigation',
        title: 'Context Preservation Across Platform',
        content: 'See how knowledge graph context flows seamlessly between platform areas - from entity analysis to stakeholder mapping to content generation.',
        position: 'right'
      },
      {
        id: 'intelligent-search',
        target: '.knowledge-search, .semantic-search-panel',
        title: 'Semantic Knowledge Search',
        content: 'Experience intelligent search that understands context and relationships, not just keywords, to surface relevant business intelligence.',
        position: 'left'
      },
      {
        id: 'knowledge-evolution',
        target: '.knowledge-learning, .graph-enhancement',
        title: 'Evolving Knowledge Intelligence',
        content: 'Discover how the knowledge graph learns and evolves, becoming more intelligent about your business context with each interaction and document.',
        position: 'center'
      }
    ]
  },

  // Epic 7.5 Content Generation Workflow
  'content-generation-workflow': {
    id: 'content-generation-workflow',
    title: 'Orchestrated Content Generation',
    description: 'Experience sophisticated multi-agent content creation workflows',
    category: 'workflow',
    epic: 7.5,
    estimatedDuration: 360,
    difficulty: 'intermediate',
    requiredFlags: ['dashboard-navigation', 'basic-agent-access'],
    steps: [
      {
        id: 'agent-coordination',
        target: '.agent-orchestration-panel, .multi-agent-workflow',
        title: 'Multi-Agent Coordination',
        content: 'Watch multiple specialized agents collaborate: Symphony orchestrates the workflow, while Nova (brand), Casey (messaging), and Echo (optimization) work together.',
        position: 'top'
      },
      {
        id: 'persona-specific-content',
        target: '.persona-content-generation, .stakeholder-specific-content',
        title: 'Persona-Specific Content Creation',
        content: 'See how content adapts automatically for different stakeholder personas - technical details for IT decision makers, ROI focus for executives, implementation timelines for operations.',
        position: 'bottom'
      },
      {
        id: 'brand-alignment',
        target: '.brand-compliance-check, .brand-intelligence-integration',
        title: 'Automated Brand Alignment',
        content: 'Experience real-time brand compliance checking and message alignment, ensuring all content maintains consistent brand voice and positioning.',
        position: 'right'
      },
      {
        id: 'content-optimization',
        target: '.content-optimization-panel, .content-quality-metrics',
        title: 'Intelligent Content Optimization',
        content: 'Discover how Echo agent continuously optimizes content for engagement, clarity, and conversion based on stakeholder feedback and performance data.',
        position: 'left'
      },
      {
        id: 'workflow-evolution',
        target: '.workflow-learning, .process-improvement',
        title: 'Self-Improving Workflows',
        content: 'See how content generation workflows learn and improve, adapting to your preferences and optimizing for better stakeholder engagement over time.',
        position: 'center'
      }
    ]
  },

  // Epic 8 Agent Gallery
  'agent-gallery': {
    id: 'agent-gallery',
    title: 'Agent Gallery Walkthrough',
    description: 'Discover and activate your specialized AI agents',
    category: 'workflow',
    epic: 8,
    estimatedDuration: 270,
    difficulty: 'intermediate',
    requiredFlags: ['agent-activation', 'workflow-basics'],
    steps: [
      {
        id: 'gallery-overview',
        target: '.agent-gallery-page',
        title: 'Agent Gallery Overview',
        content: 'Browse through 51+ specialized agents, each designed for specific business functions.',
        position: 'center'
      },
      {
        id: 'agent-categories',
        target: '.agent-filter-controls',
        title: 'Agent Categories',
        content: 'Filter agents by category to find exactly what you need for your workflow.',
        position: 'top'
      },
      {
        id: 'agent-card',
        target: '.agent-card:first-child',
        title: 'Agent Information',
        content: 'Each agent card shows capabilities, status, and activation options.',
        position: 'bottom'
      },
      {
        id: 'agent-activation',
        target: '.agent-activation-button',
        title: 'Activate Agents',
        content: 'Click activate to add agents to your active workflow toolkit.',
        position: 'top',
        action: 'hover'
      },
      {
        id: 'agent-management',
        target: '.agent-management-panel',
        title: 'Manage Active Agents',
        content: 'Monitor and control your active agents from the management panel.',
        position: 'right'
      }
    ]
  },

  // Epic 8 Cross-Component Navigation
  'cross-component-navigation': {
    id: 'cross-component-navigation',
    title: 'Platform Navigation Flow',
    description: 'Learn seamless navigation across platform components',
    category: 'workflow',
    epic: 8,
    estimatedDuration: 300,
    difficulty: 'intermediate',
    requiredFlags: ['dashboard-navigation', 'agent-activation'],
    steps: [
      {
        id: 'start-dashboard',
        target: '.dashboard-header',
        title: 'Starting Point: Dashboard',
        content: 'Your dashboard is the central hub for all platform activities.',
        position: 'bottom'
      },
      {
        id: 'navigate-agents',
        target: '[href="/agents"]',
        title: 'Navigate to Agents',
        content: 'Click to explore your agent gallery and activate specialized AI assistants.',
        position: 'bottom',
        action: 'click'
      },
      {
        id: 'agents-to-dashboard',
        target: '.breadcrumb-navigation',
        title: 'Breadcrumb Navigation',
        content: 'Use breadcrumbs to navigate back or understand your current location.',
        position: 'top'
      },
      {
        id: 'account-access',
        target: '[href="/account"]',
        title: 'Account Management',
        content: 'Access account settings for organization and user management.',
        position: 'left',
        action: 'hover'
      },
      {
        id: 'navigation-menu-demo',
        target: '.main-navigation',
        title: 'Full Navigation Menu',
        content: 'The navigation menu provides access to all platform capabilities.',
        position: 'right'
      }
    ]
  },

  // Epic 8 Agent Activation (Enhanced)
  'agent-activation': {
    id: 'agent-activation',
    title: 'Agent Activation Walkthrough',
    description: 'Learn how to activate and coordinate your AI agents',
    category: 'workflow',
    epic: 8,
    estimatedDuration: 240,
    difficulty: 'intermediate',
    requiredFlags: ['agent-activation', 'workflow-basics'],
    steps: [
      {
        id: 'agent-discovery',
        target: '.agent-gallery',
        title: 'Discover Your Agents',
        content: 'Browse through 51+ specialized agents, each designed for specific business functions.',
        position: 'center'
      },
      {
        id: 'agent-selection',
        target: '.agent-card:first-child',
        title: 'Select an Agent',
        content: 'Click on any agent to see its capabilities and activation options.',
        position: 'top',
        action: 'click'
      },
      {
        id: 'workflow-basics',
        target: '.workflow-panel',
        title: 'Basic Workflow Creation',
        content: 'Create simple agent workflows by connecting agents with your business objectives.',
        position: 'left'
      }
    ]
  },
  
  // Epic 9 Workflow Orchestration
  'agent-workflow-orchestration': {
    id: 'agent-workflow-orchestration',
    title: 'Visual Agent Workflow Creation',
    description: 'Master multi-agent workflow orchestration',
    category: 'orchestration',
    epic: 9,
    estimatedDuration: 300,
    difficulty: 'advanced',
    requiredFlags: ['workflow-canvas', 'agent-collaboration', 'multi-agent-handoffs'],
    steps: [
      {
        id: 'workflow-canvas',
        target: '.workflow-canvas',
        title: 'Visual Workflow Canvas',
        content: 'Create sophisticated multi-agent workflows using our visual canvas interface.',
        position: 'center'
      },
      {
        id: 'agent-collaboration',
        target: '.agent-collaboration-panel',
        title: 'Agent Collaboration',
        content: 'Configure how agents work together and share information in your workflows.',
        position: 'right'
      },
      {
        id: 'multi-agent-handoffs',
        target: '.handoff-configuration',
        title: 'Multi-Agent Handoffs',
        content: 'Set up seamless handoffs between agents for complex business processes.',
        position: 'bottom'
      }
    ]
  },
  
  // Epic 10 Ecosystem Command Center
  'ecosystem-command-center': {
    id: 'ecosystem-command-center',
    title: 'Navigate Your Complete Ecosystem',
    description: 'Master the ecosystem command center with Emma\'s intelligence',
    category: 'advanced',
    epic: 10,
    estimatedDuration: 360,
    difficulty: 'advanced',
    requiredFlags: ['ecosystem-navigator', 'emma-orchestrator', 'command-center'],
    steps: [
      {
        id: 'ecosystem-navigator',
        target: '.ecosystem-navigator',
        title: 'Ecosystem Navigation',
        content: 'Switch between different ecosystem entities and manage multiple business contexts.',
        position: 'top'
      },
      {
        id: 'emma-orchestrator',
        target: '.emma-panel',
        title: 'Emma Intelligence',
        content: 'Emma provides real-time recommendations and orchestrates complex ecosystem interactions.',
        position: 'left'
      },
      {
        id: 'command-center',
        target: '.command-center-controls',
        title: 'Command Center Controls',
        content: 'Access advanced controls for ecosystem-wide coordination and intelligence.',
        position: 'bottom'
      }
    ]
  },
  
  // Epic 11 Triad Orchestration
  'triad-orchestration': {
    id: 'triad-orchestration',
    title: 'Customer-Vendor-Partner Coordination',
    description: 'Orchestrate complex triad relationships with AI intelligence',
    category: 'advanced',
    epic: 11,
    estimatedDuration: 420,
    difficulty: 'advanced',
    requiredFlags: ['triad-orchestration', 'customer-vendor-partner', 'emma-coordination'],
    steps: [
      {
        id: 'triad-workspace',
        target: '.triad-workspace',
        title: 'Triad Coordination Workspace',
        content: 'Coordinate complex relationships between customers, vendors, and partners in one unified interface.',
        position: 'center'
      },
      {
        id: 'customer-vendor-partner',
        target: '.relationship-mapping',
        title: 'Relationship Mapping',
        content: 'Visualize and manage intricate business relationships across your entire ecosystem.',
        position: 'right'
      },
      {
        id: 'emma-coordination',
        target: '.emma-coordination-panel',
        title: 'Emma\'s Coordination Intelligence',
        content: 'Emma analyzes relationship dynamics and recommends optimal coordination strategies.',
        position: 'bottom'
      }
    ]
  },

  // INVESTOR DEMO SCENARIOS
  'investor-platform-demo': {
    id: 'investor-platform-demo',
    title: 'Investor Platform Demonstration',
    description: 'Comprehensive platform demonstration showcasing CrowsNest capabilities for investors',
    category: 'advanced',
    epic: 8,
    estimatedDuration: 600,
    difficulty: 'beginner',
    requiredFlags: ['demo-mode'],
    steps: [
      {
        id: 'platform-intro',
        target: '.dashboard-header',
        title: 'Welcome to Cosellus',
        content: 'Cosellus is an intelligent AI marketing orchestration platform featuring 35+ specialized agents that transform marketing operations through coordinated intelligence.',
        position: 'center'
      },
      {
        id: 'ecosystem-overview',
        target: '.platform-metrics-widget',
        title: 'Ecosystem Intelligence Dashboard',
        content: 'Real-time monitoring of your intelligent ecosystem with performance metrics, agent activity, and system health indicators.',
        position: 'top'
      },
      {
        id: 'agent-ecosystem',
        target: '[href="/agents"]',
        title: '51+ Specialized AI Agents',
        content: 'Browse our comprehensive agent marketplace featuring specialized agents for marketing, sales, operations, and strategic intelligence.',
        position: 'bottom',
        action: 'hover'
      },
      {
        id: 'roi-demonstration',
        target: '.system-status-widget',
        title: 'Measurable ROI & Performance',
        content: 'Track tangible business impact with real-time performance metrics, productivity gains, and operational efficiency improvements.',
        position: 'left'
      },
      {
        id: 'scalability-showcase',
        target: '.agent-activity-widget',
        title: 'Enterprise-Scale Orchestration',
        content: 'Demonstrate how CrowsNest scales from startup agility to enterprise complexity with intelligent agent coordination.',
        position: 'right'
      },
      {
        id: 'competitive-advantage',
        target: '.navigation-menu',
        title: 'Competitive Market Position',
        content: 'CrowsNest uniquely combines agent specialization, ecosystem orchestration, and real-time intelligence for unprecedented business transformation.',
        position: 'bottom'
      }
    ]
  },

  'multi-tenant-demo': {
    id: 'multi-tenant-demo',
    title: 'Multi-Tenant Enterprise Demonstration',
    description: 'Showcase enterprise multi-tenant capabilities for complex organizational structures',
    category: 'advanced',
    epic: 8,
    estimatedDuration: 480,
    difficulty: 'intermediate',
    requiredFlags: ['demo-mode', 'dashboard-navigation'],
    steps: [
      {
        id: 'tenant-switching',
        target: '.organization-setup',
        title: 'Enterprise Tenant Management',
        content: 'Seamlessly switch between multiple organizations, subsidiaries, and partner ecosystems within one unified platform.',
        position: 'top'
      },
      {
        id: 'role-based-access',
        target: '.user-profile-widget',
        title: 'Role-Based Intelligence Access',
        content: 'Sophisticated permission management ensures users access only relevant agents and data for their organizational role.',
        position: 'bottom'
      },
      {
        id: 'cross-tenant-collaboration',
        target: '.agent-activity-widget',
        title: 'Cross-Tenant Agent Coordination',
        content: 'Enable secure collaboration between different organizational entities while maintaining data sovereignty and security.',
        position: 'left'
      },
      {
        id: 'enterprise-scaling',
        target: '.platform-metrics-widget',
        title: 'Enterprise-Scale Performance',
        content: 'Platform demonstrates enterprise-grade performance supporting thousands of users across multiple organizational hierarchies.',
        position: 'right'
      }
    ]
  },

  'agent-ecosystem-showcase': {
    id: 'agent-ecosystem-showcase',
    title: 'AI Agent Ecosystem Showcase',
    description: 'Demonstrate the power and specialization of the 51+ agent ecosystem',
    category: 'workflow',
    epic: 8,
    estimatedDuration: 540,
    difficulty: 'intermediate',
    requiredFlags: ['demo-mode', 'agent-activation'],
    steps: [
      {
        id: 'agent-specialization',
        target: '.agent-gallery',
        title: 'Specialized Agent Intelligence',
        content: 'Each of our 51+ agents is purpose-built for specific business functions: Nova for brand intelligence, Casey for messaging, Riley for research, and more.',
        position: 'center'
      },
      {
        id: 'agent-coordination',
        target: '.agent-management-panel',
        title: 'Intelligent Agent Orchestration',
        content: 'Watch agents work together seamlessly - passing tasks, sharing insights, and coordinating complex business processes automatically.',
        position: 'right'
      },
      {
        id: 'real-time-collaboration',
        target: '.workflow-panel',
        title: 'Real-Time Agent Collaboration',
        content: 'Agents collaborate in real-time, creating dynamic workflows that adapt to changing business needs and priorities.',
        position: 'top'
      },
      {
        id: 'business-impact',
        target: '.agent-activity-widget',
        title: 'Measurable Business Impact',
        content: 'Agent activities translate directly to business outcomes: increased conversion rates, reduced operational costs, accelerated decision-making.',
        position: 'bottom'
      }
    ]
  },

  'platform-roi-demo': {
    id: 'platform-roi-demo',
    title: 'Platform ROI & Value Demonstration',
    description: 'Showcase tangible business value and return on investment metrics',
    category: 'advanced',
    epic: 8,
    estimatedDuration: 420,
    difficulty: 'beginner',
    requiredFlags: ['demo-mode'],
    steps: [
      {
        id: 'productivity-gains',
        target: '.platform-metrics-widget',
        title: 'Productivity Multiplication',
        content: 'CrowsNest customers typically see 300-500% productivity increases through intelligent agent automation and coordination.',
        position: 'top'
      },
      {
        id: 'operational-efficiency',
        target: '.system-status-widget',
        title: 'Operational Cost Reduction',
        content: 'Automated agent workflows reduce operational costs by 40-60% while improving output quality and consistency.',
        position: 'bottom'
      },
      {
        id: 'decision-acceleration',
        target: '.agent-activity-widget',
        title: 'Decision-Making Acceleration',
        content: 'Real-time intelligence gathering and analysis accelerates strategic decision-making from weeks to hours.',
        position: 'left'
      },
      {
        id: 'market-responsiveness',
        target: '.dashboard-header',
        title: 'Market Responsiveness',
        content: 'Continuous market monitoring and automated response capabilities provide significant competitive advantages.',
        position: 'center'
      },
      {
        id: 'scalable-growth',
        target: '.navigation-menu',
        title: 'Scalable Growth Foundation',
        content: 'Platform grows with your business - from startup agility to enterprise scale without losing efficiency or intelligence.',
        position: 'right'
      }
    ]
  },

  'real-time-collaboration-demo': {
    id: 'real-time-collaboration-demo',
    title: 'Real-Time Collaboration Demonstration',
    description: 'Showcase live collaboration capabilities and real-time intelligence coordination',
    category: 'workflow',
    epic: 8,
    estimatedDuration: 360,
    difficulty: 'intermediate',
    requiredFlags: ['demo-mode', 'workflow-basics'],
    steps: [
      {
        id: 'live-collaboration',
        target: '.agent-activity-widget',
        title: 'Live Agent Collaboration',
        content: 'Watch agents collaborate in real-time across different business functions, sharing insights and coordinating responses.',
        position: 'center'
      },
      {
        id: 'websocket-integration',
        target: '.system-status-widget',
        title: 'Real-Time System Integration',
        content: 'WebSocket-powered real-time updates ensure all stakeholders see the same information simultaneously across the organization.',
        position: 'top'
      },
      {
        id: 'cross-functional-coordination',
        target: '.workflow-panel',
        title: 'Cross-Functional Intelligence',
        content: 'Agents from marketing, sales, operations, and strategy coordinate seamlessly to deliver unified business outcomes.',
        position: 'bottom'
      },
      {
        id: 'adaptive-workflows',
        target: '.agent-management-panel',
        title: 'Adaptive Workflow Intelligence',
        content: 'Workflows adapt automatically to changing conditions, priorities, and market dynamics without human intervention.',
        position: 'right'
      }
    ]
  }
};

// Epic-based Feature Flag Configurations
export const EPIC_FEATURE_FLAGS: Record<number, Partial<FeatureFlags>> = {
  7.5: {
    'dashboard-navigation': true,
    'basic-agent-access': true,
    'brand-intelligence': true
  },
  8: {
    'agent-activation': true,
    'workflow-basics': true,
    'demo-mode': true
  },
  9: {
    'workflow-canvas': true,
    'agent-collaboration': true,
    'multi-agent-handoffs': true
  },
  10: {
    'ecosystem-navigator': true,
    'emma-orchestrator': true,
    'command-center': true
  },
  11: {
    'triad-orchestration': true,
    'customer-vendor-partner': true,
    'emma-coordination': true
  },
  12: {
    'advanced-analytics': true
  },
  13: {
    'ai-recommendations': true
  },
  14: {
    'enterprise-integration': true
  },
  15: {
    'marketplace-access': true
  },
  16: {
    'global-orchestration': true
  }
};
